import { useState } from 'react';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import {
  useApproveChangeRequestMutation,
  useChangeRequestsQuery,
  useDeclineChangeRequestMutation,
} from '../store/api';

function decisionError(error: unknown): string {
  const maybeQueryError = error as FetchBaseQueryError | SerializedError;
  if (
    'data' in maybeQueryError &&
    maybeQueryError.data &&
    typeof maybeQueryError.data === 'object' &&
    (maybeQueryError.data as { error_code?: string }).error_code ===
      'slot_unavailable'
  ) {
    return 'That requested time was just taken — decline the request or ask the patient to pick again.';
  }
  return "We couldn't apply that decision. Please try again.";
}

function formatRequestedTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

export interface RescheduleRequestBannerProps {
  appointmentId: string;
}

/** Detail-drawer banner for a pending reschedule request (GDN-2452).
 *
 * Mirrors the requests-queue decision: approve moves the appointment to the
 * requested time, decline keeps the original. No identity step — the
 * appointment's patient/prospect is already resolved.
 */
export function RescheduleRequestBanner({
  appointmentId,
}: RescheduleRequestBannerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useChangeRequestsQuery();
  const [approveChangeRequest, { isLoading: isApproving }] =
    useApproveChangeRequestMutation();
  const [declineChangeRequest, { isLoading: isDeclining }] =
    useDeclineChangeRequestMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const request = (data?.items ?? []).find(
    item =>
      item.appointment_id === appointmentId &&
      item.status === 'pending' &&
      item.request_type === 'reschedule',
  );
  if (!request) return null;

  const busy = isApproving || isDeclining;
  const requestedTime = request.requested_start_time
    ? formatRequestedTime(request.requested_start_time)
    : null;

  const handleApprove = () => {
    setErrorMessage(null);
    approveChangeRequest({ requestId: request.appointment_change_request_id })
      .unwrap()
      .then(() => enqueueSnackbar('New time confirmed', { variant: 'success' }))
      .catch((error: unknown) => setErrorMessage(decisionError(error)));
  };

  const handleDecline = () => {
    setErrorMessage(null);
    declineChangeRequest({ requestId: request.appointment_change_request_id })
      .unwrap()
      .then(() =>
        enqueueSnackbar('Request declined — original time kept', {
          variant: 'success',
        }),
      )
      .catch((error: unknown) => setErrorMessage(decisionError(error)));
  };

  return (
    <Alert
      severity="warning"
      icon={false}
      sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Reschedule requested
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {requestedTime
              ? `${request.requester_name || 'The patient'} asked to move this to ${requestedTime}.`
              : `${request.requester_name || 'The patient'} asked to move this appointment.`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={busy}
            startIcon={
              isApproving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CheckCircleOutlinedIcon />
              )
            }
            onClick={handleApprove}
          >
            Confirm
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            disabled={busy}
            startIcon={
              isDeclining ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CloseIcon />
              )
            }
            onClick={handleDecline}
          >
            Decline
          </Button>
        </Stack>
      </Box>
      {errorMessage && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {errorMessage}
        </Typography>
      )}
    </Alert>
  );
}
